import { useCallback } from 'react'
import { useQuery } from 'convex/react'
import { useAuthActions as useConvexAuthActions } from '@convex-dev/auth/react'
import { api } from '@backend/_generated/api'
import { useAuthStore } from '@/stores/authStore'
import { env } from '@/env'

/**
 * Auth action hook — signIn, signOut.
 *
 * signIn navigates to /sign-in (the sign-in page handles the actual Convex Auth flow).
 * signOut delegates to Convex Auth and resets local state.
 */
export const useAuthActions = () => {
  const { signOut: convexSignOut } = useConvexAuthActions()
  const setIsLoggingOut = useAuthStore((s) => s.setIsLoggingOut)
  const reset = useAuthStore((s) => s.reset)
  const setCurrentOrganization = useAuthStore((s) => s.setCurrentOrganization)

  const organizations = useQuery(api.profile.getMyOrganizations)

  const signIn = useCallback((redirectTo?: string) => {
    const path =
      '/sign-in' +
      (redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : '')
    window.location.href = path
  }, [])

  const signOut = useCallback(async () => {
    setIsLoggingOut(true)
    reset()
    window.location.href = env.VITE_MARKETING_URL ?? '/'
    await convexSignOut()
  }, [convexSignOut, reset, setIsLoggingOut])

  const switchOrganization = useCallback(
    async (orgId: string) => {
      if (!organizations)
        return { success: false, error: 'Organizations not loaded' }
      const org = organizations.find((o) => o.organizationId === orgId)
      if (!org) return { success: false, error: 'Organization not found' }
      setCurrentOrganization({
        organizationId: org.organizationId,
        isAdmin: org.isAdmin,
        roles: org.roles,
      })
      return { success: true }
    },
    [organizations, setCurrentOrganization],
  )

  return { signIn, signOut, switchOrganization }
}
